/**
 * ProductSelector - Horizontal tab/chip selector for SKUs
 *
 * Pattern from previous version screenshots:
 * - Horizontal row of product chips
 * - Each chip: Product name (2 lines max) + Price
 * - Selected state: teal/primary border highlight
 * - Scrollable on mobile
 */

interface Product {
  id: string;
  name: string;
  price: string | number;
  currency?: string;
  /** Optional subtitle (e.g., size, variant) */
  subtitle?: string;
}

interface ProductSelectorProps {
  products: Product[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** Format price display */
  formatPrice?: (price: number | string, currency?: string) => string;
  className?: string;
}

export function ProductSelector({
  products,
  selectedId,
  onSelect,
  formatPrice = defaultFormatPrice,
  className = '',
}: ProductSelectorProps) {
  return (
    <div className={`overflow-x-auto ${className}`}>
      <div className="flex gap-3 min-w-max pb-2">
        {products.map((product) => {
          const isSelected = product.id === selectedId;

          return (
            <button
              key={product.id}
              onClick={() => onSelect(product.id)}
              className={`
                min-w-[140px] p-3 rounded-lg border text-left transition-all
                ${isSelected
                  ? 'bg-primary-500/10 border-primary-500 ring-1 ring-primary-500/30'
                  : 'bg-surface-800 border-surface-600 hover:border-surface-500'
                }
              `}
            >
              {/* Product Name */}
              <div className={`font-medium text-sm line-clamp-2 ${isSelected ? 'text-primary-300' : 'text-white'}`}>
                {product.name}
              </div>

              {/* Subtitle (if any) */}
              {product.subtitle && (
                <div className="text-xs text-text-tertiary mt-0.5">
                  {product.subtitle}
                </div>
              )}

              {/* Price */}
              <div className={`text-xs mt-1 ${isSelected ? 'text-primary-400' : 'text-text-secondary'}`}>
                {formatPrice(product.price, product.currency)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Default price formatter
 */
function defaultFormatPrice(price: number | string, currency?: string): string {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numPrice)) return String(price);

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numPrice);

  return currency ? `${currency} ${formatted}` : `$${formatted}`;
}

/**
 * ProductSelectorCompact - Smaller chips for dense layouts
 */
interface ProductSelectorCompactProps extends Omit<ProductSelectorProps, 'formatPrice'> {
  showPrice?: boolean;
}

export function ProductSelectorCompact({
  products,
  selectedId,
  onSelect,
  showPrice = false,
  className = '',
}: ProductSelectorCompactProps) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {products.map((product) => {
        const isSelected = product.id === selectedId;

        return (
          <button
            key={product.id}
            onClick={() => onSelect(product.id)}
            className={`
              px-3 py-1.5 rounded-lg border text-sm transition-all
              ${isSelected
                ? 'bg-primary-500/10 border-primary-500 text-primary-300'
                : 'bg-surface-800 border-surface-600 text-text-secondary hover:border-surface-500 hover:text-white'
              }
            `}
          >
            {product.name}
            {showPrice && (
              <span className="ml-1 opacity-75">
                ({typeof product.price === 'number' ? `$${product.price.toLocaleString()}` : product.price})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ProductSelectorDropdown - Dropdown alternative for many products
 */
interface ProductSelectorDropdownProps {
  products: Product[];
  selectedId: string;
  onSelect: (id: string) => void;
  label?: string;
  className?: string;
}

export function ProductSelectorDropdown({
  products,
  selectedId,
  onSelect,
  label = 'Select Product',
  className = '',
}: ProductSelectorDropdownProps) {
  const selectedProduct = products.find(p => p.id === selectedId);

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-text-tertiary uppercase tracking-wide mb-1">
          {label}
        </label>
      )}
      <select
        value={selectedId}
        onChange={(e) => onSelect(e.target.value)}
        className="w-full bg-surface-800 border border-surface-600 rounded-lg px-3 py-2 text-white text-sm focus:border-primary-500 focus:outline-none"
      >
        {products.map((product) => (
          <option key={product.id} value={product.id}>
            {product.name} - {typeof product.price === 'number' ? `$${product.price.toLocaleString()}` : product.price}
          </option>
        ))}
      </select>
    </div>
  );
}
